import React, { useState, useEffect, useRef, useContext } from "react"
import { Link } from "react-router-dom"
import { SmartDateFormat } from "../../util/dates"
import { PauseCircle, PlayCircle, PlusCircle } from "react-bootstrap-icons"
import { Spinner } from "../shared/Spinner"
import { RequestError } from "../shared/RequestError"
import { compareByKey } from "../../util/utils"
import CommentSection from "../comments/CommentSection"
import { useDispatch, useSelector } from "react-redux"
import {
  playNext,
  pushToQueue,
  playOrPause,
  prependAndPlay,
  loadTrack,
} from "../../stores/mediaPlayerSlice"
import { SingleTrackItem } from "./SingleTrackItem"
import { PostListItemExpandedStyles } from "./constants"
import { usePost } from "../../hooks/usePost"
import { ShareUrlButton } from "../ShareUrlButton"
import { AnchorButton, ShowHideButton } from "../shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import RelatedPosts from "../RelatedPosts"
import PremiereHeader from "./PremiereHeader"
import clsx from "clsx"
import styles from "./styles.module.css"
import { StringParam, useQueryParams } from "use-query-params"
import { UserContext } from "../HomeApp"
import EditManageHeader from "../EditManageHeader"

const ICON_SIZE = "1.6rem"
const MARGIN_SPACING = "0rem 0.5rem 0rem 0"

export const AlbumListItemExpanded = ({
  album,
  updateStatus,
  adminMode = false,
  isPostPage = false,
}) => {
  const metaClass = isPostPage ? "col-span-12 pt-2" : PostListItemExpandedStyles.metaDiv
  const commentsClass = isPostPage ? "col-span-12 pt-4" : PostListItemExpandedStyles.commentsDiv
  const [postStatus, setPostStatus] = useState(album.status)
  const [showEditForm, setShowEditForm] = useState(false)
  const { user } = useContext(UserContext)
  const isOwner = user && user.band && user.band.id === album.band_id
  const albumDetailData = usePost(album.id, "songs")
  var albumDetails = albumDetailData?.data?.post

  const [queryParams] = useQueryParams({
    song_id: StringParam,
  })

  const dispatch = useDispatch()
  const currentMediaItem = useSelector(
    // @ts-ignore
    (state) => state.mediaPlayer.value.currentMediaItem,
  )

  // @ts-ignore
  const playerState = useSelector(
    (state) => state.mediaPlayer.value.playerState,
  )
  const isCurrentlyPlaying =
    playerState === "playing" && currentMediaItem?.albumId === album.id
  const isCurrentlyQueued =
    playerState === "paused" && currentMediaItem?.albumId === album.id

  const onPlayClick = (e) => {
    dispatch(prependAndPlay(sortedSongs()))
  }
  const onPlayOrPauseClick = (e) => {
    dispatch(playOrPause())
  }

  const onAddClick = (e) => {
    dispatch(pushToQueue(sortedSongs()))
  }

  const sortedSongs = () => {
    return [...albumDetails.songs]
      .sort((a, b) => compareByKey(a, b, "track_number"))
      .map((song) => ({
        ...song,
        album_name: albumDetails.title,
        cover_image_small: albumDetails.cover_image_small,
      }))
  }

  // Auto-play song if song_id parameter is present
  const albumStatus = albumDetailData?.status
  const albumSongs = albumDetails?.songs
  const albumTitle = albumDetails?.title
  const albumCoverImage = albumDetails?.cover_image_small
  const songIdParam = queryParams.song_id
  const hasAutoPlayedRef = useRef(false)

  useEffect(() => {
    // Reset ref when song_id parameter changes
    hasAutoPlayedRef.current = false
  }, [songIdParam])

  useEffect(() => {
    if (
      albumStatus === "success" &&
      albumSongs &&
      songIdParam &&
      albumTitle &&
      !hasAutoPlayedRef.current
    ) {
      const songId = parseInt(songIdParam, 10)
      const targetSong = albumSongs.find((song) => song.id === songId)

      if (targetSong) {
        hasAutoPlayedRef.current = true
        const mappedSong = [
          {
            ...targetSong,
            album_name: albumTitle,
            cover_image_small: albumCoverImage,
          },
        ]
        dispatch(loadTrack(mappedSong))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumStatus, songIdParam, dispatch])

  return (
    <div className="grid grid-cols-12">
      <PremiereHeader post={album} size="large" />
      <div className={PostListItemExpandedStyles.contentDiv}>
        <img
          src={album.external_photo_src}
          alt={album.title}
          className="w-full"
          style={{ aspectRatio: "1/1" }}
        />
      </div>
      <div className={metaClass}>
        <EditManageHeader
          isOwner={isOwner}
          post={album}
          setShowEditForm={setShowEditForm}
        />
        <div className="flex">
          <div className="flex flex-none">
            {isCurrentlyPlaying ? (
              <PauseCircle
                className="cursor-pointer"
                style={{
                  minWidth: ICON_SIZE,
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  margin: MARGIN_SPACING,
                }}
                onClick={onPlayOrPauseClick}
              />
            ) : (
              <PlayCircle
                className="cursor-pointer"
                style={{
                  minWidth: ICON_SIZE,
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  margin: MARGIN_SPACING,
                }}
                onClick={isCurrentlyQueued ? onPlayOrPauseClick : onPlayClick}
              />
            )}
            <PlusCircle
              className="cursor-pointer"
              style={{
                minWidth: ICON_SIZE,
                width: ICON_SIZE,
                height: ICON_SIZE,
                margin: MARGIN_SPACING,
              }}
              onClick={onAddClick}
            />
          </div>

          <div className="flex items-start justify-between flex-grow">
            <h2
              className={clsx(
                PostListItemExpandedStyles.header,
                "inline mr-2",
                album.is_bn_premiere && styles.premiereTitle,
              )}
              dangerouslySetInnerHTML={{ __html: album.title }}
            ></h2>
            <ShareUrlButton nadaUrl={album.nada_url} renderMode="button" />
          </div>
        </div>
        <h3>
          <Link
            className={PostListItemExpandedStyles.bandName}
            to={album.local_path}
          >
            {album.band_name}
          </Link>
        </h3>
        <div className="text-xs">
          {!album.is_released ? (
            <div>
              Announced&nbsp;
              {SmartDateFormat({
                dateStr: album.announced_at,
                includeTime: false,
              })}
              &nbsp;for Release on&nbsp;
              {SmartDateFormat({
                dateStr: album.published_at,
                includeTime: false,
              })}
            </div>
          ) : (
            <div>
              Released&nbsp;
              {SmartDateFormat({
                dateStr: album.published_at,
                includeTime: false,
              })}
            </div>
          )}
        </div>
        <div className={PostListItemExpandedStyles.externalLink}>
          {album.class_name === "Bandcamp" ? (
            <div className="mt-1">
              <AnchorButton href={album.external_url} target="_blank">
                Buy on&nbsp;
                {album.class_name}
              </AnchorButton>
            </div>
          ) : album.class_name === "BandNada" &&
            album.bn_exclusive_until &&
            new Date(album.bn_exclusive_until) > new Date() ? (
            <span className="font-bold text-bn-premiere-green">
              exclusively on bandNada
            </span>
          ) : (
            <a href={album.external_url} target="_blank" rel="noreferrer">
              view on&nbsp;
              {album.class_name}
            </a>
          )}
        </div>

        {albumDetails && (albumDetails.about || albumDetails.credits) && (
          <div className="mt-4 text-xs text-gray-700 space-y-3">
            {albumDetails.about && (
              <p className="whitespace-pre-line mb-2">{albumDetails.about}</p>
            )}
            {albumDetails.credits && (
              <p className="whitespace-pre-line">{albumDetails.credits}</p>
            )}
          </div>
        )}
        <div className="mt-4">
          {albumDetailData.status === "loading" ? (
            <Spinner className="text-gray-500" />
          ) : albumDetailData.status === "error" ? (
            <RequestError error={albumDetailData.error} />
          ) : (
            <>
              {albumDetails.songs?.length > 0 ? (
                <>
                  <ul className="text-black">
                    {sortedSongs().map((song, index) => (
                      <SingleTrackItem
                        key={song.id}
                        song={song}
                        index={index}
                      />
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div className={commentsClass}>
        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}

        <RelatedPosts postId={album.id} className="mb-4" />

        <ReactionBlock
          contentId={album.id}
          contentType="Post"
          postId={album.id}
        />

        <div className="mb-4" />

        <CommentSection
          postId={album.id}
          headerText="The Peanut Gallery Says:"
          className="mb-4"
        />
      </div>
    </div>
  )
}
